import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, GraduationCap, Trophy, Award, CheckCircle2, Star } from "lucide-react";

export default function SaasSolutions() {
  const [activeTab, setActiveTab] = useState("schools");

  const solutions = {
    schools: {
      title: "Schools",
      tagline: "Simplify classroom administration & keep parents engaged.",
      icon: <BookOpen size={24} />,
      color: "violet",
      gradient: "from-violet-500 to-purple-600",
      features: [
        { name: "Automated Attendance", desc: "Easy student attendance tracking with instant SMS & email notifications to parents." },
        { name: "Dedicated Parent Portal", desc: "Real-time updates on grades, class behavior, notifications, and fees." },
        { name: "Smart Homework Planner", desc: "Easily distribute worksheets, assignments, and digital resources to classrooms." }
      ],
      previewImg: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    },
    colleges: {
      title: "Colleges",
      tagline: "Empower departments and track graduation achievements.",
      icon: <GraduationCap size={24} />,
      color: "blue",
      gradient: "from-blue-500 to-indigo-600",
      features: [
        { name: "Placement Management", desc: "Coordinate recruitment schedules, resumes, mock interviews, and student applications." },
        { name: "Deep Student Analytics", desc: "Identify students at risk, track credit progress, and department performance." },
        { name: "Automated Term Exams", desc: "Secure mid-term & final examinations with facial recognition and browser lockdowns." }
      ],
      previewImg: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    },
    coaching: {
      title: "Coaching Institutes",
      tagline: "Accelerate exam preparation with smart mock tests.",
      icon: <Trophy size={24} />,
      color: "cyan",
      gradient: "from-cyan-500 to-blue-600",
      features: [
        { name: "Competitive Mock Tests", desc: "National ranking, multi-chapter test series, detailed solutions, and answer key releases." },
        { name: "AI Question Generator", desc: "Instantly compile mock exams from a database of millions of curriculum questions." },
        { name: "Performance Leaderboards", desc: "Gamified dashboards that encourage peer-to-peer competition and study sprint targets." }
      ],
      previewImg: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    },
    training: {
      title: "Training Centers",
      tagline: "Provide certifications and continuous upskilling.",
      icon: <Award size={24} />,
      color: "emerald",
      gradient: "from-emerald-500 to-teal-600",
      features: [
        { name: "Custom Certifications", desc: "Issue automated, secure, and verifiable digital certificates on course completion." },
        { name: "Corporate Employee Learning", desc: "Self-paced paths, corporate dashboards, and manager tracking permissions." },
        { name: "Practical Assessments", desc: "Practical programming environments, labs, and situational quizzes." }
      ],
      previewImg: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
    }
  };

  const getBorderColor = (col) => {
    if (col === "violet") return "border-violet-500 text-violet-600 bg-violet-50 dark:bg-violet-950/20";
    if (col === "blue") return "border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-950/20";
    if (col === "cyan") return "border-cyan-500 text-cyan-600 bg-cyan-50 dark:bg-cyan-950/20";
    return "border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20";
  };

  return (
    <section id="solutions" className="py-24 bg-white dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 dark:bg-violet-950/40 border border-violet-100 dark:border-violet-900/50 rounded-full text-violet-600 dark:text-violet-400 text-sm font-bold mb-4">
            <Star size={16} />
            <span>Targeted Workflows</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Tailored Solutions for Every Institute
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Whether you run a local primary school, a state university, or an EdTech business, Skillzy provides custom configurations suited for your operations.
          </p>
        </div>

        {/* Tab Switcher Grid */}
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          {/* Tabs menu */}
          <div className="lg:col-span-4 space-y-3">
            {Object.keys(solutions).map((key) => {
              const sol = solutions[key];
              const isSelected = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border text-left transition-all ${
                    isSelected
                      ? getBorderColor(sol.color) + " shadow-lg"
                      : "border-slate-100 dark:border-slate-800 hover:border-slate-200 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <div className={`p-3 rounded-xl ${isSelected ? "bg-white dark:bg-slate-900 text-current" : "bg-white dark:bg-slate-950 text-slate-400"} shadow-sm shrink-0`}>
                    {sol.icon}
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base">{sol.title}</h3>
                    <p className="text-xs opacity-80 mt-1 line-clamp-1">{sol.tagline}</p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Solutions Content Panel */}
          <div className="lg:col-span-8 bg-slate-50 dark:bg-slate-900/50 rounded-[2.5rem] border border-slate-100 dark:border-slate-850 p-8 sm:p-12 shadow-sm min-h-[480px] flex flex-col justify-between overflow-hidden relative">
            
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
                className="grid md:grid-cols-12 gap-8 items-center flex-1"
              >
                {/* Features List */}
                <div className="md:col-span-7 space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white leading-tight">
                    Skillzy for {solutions[activeTab].title}
                  </h3>
                  <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {solutions[activeTab].tagline}
                  </p>

                  <div className="space-y-4 pt-4">
                    {solutions[activeTab].features.map((feat) => (
                      <div key={feat.name} className="flex gap-3">
                        <CheckCircle2 className={`w-5 h-5 text-slate-900 dark:text-white shrink-0 mt-0.5`} />
                        <div>
                          <h4 className="text-sm font-extrabold text-slate-800 dark:text-slate-200">{feat.name}</h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{feat.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Feature Graphic/Image */}
                <div className="md:col-span-5 relative h-64 md:h-80 w-full rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-800 shrink-0">
                  <img
                    src={solutions[activeTab].previewImg}
                    alt={solutions[activeTab].title}
                    className="w-full h-full object-cover opacity-90 dark:opacity-85 hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-905/70 via-transparent to-transparent" />
                </div>
              </motion.div>
            </AnimatePresence>

          </div>

        </div>

      </div>
    </section>
  );
}
